/* eslint-disable max-lines-per-function */
/* eslint-disable no-console */

'use client';

import React, {
  useEffect,
  useState,
  useRef,
  MutableRefObject,
  useMemo,
  memo,
} from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';

import Intercom from '@intercom/messenger-js-sdk';
import { useSession } from 'next-auth/react';

import { cancelUnlimitTxn, fetchConfirmationPayphone } from '@/features/deposit/services';
import { messages } from '@/localization';

import { useAuthStore } from '@/stores/userStore';
import { userUIStore } from '@/stores/userUIStore';

import { decrypt, getDatGames } from '@/utils/helpers';

import { CarouselData } from '@/interfaces/core';

import LeagueSlider from '../banner/LeagueSlider';
import SWSmoothCarousel from '../banner/SWSmoothCarousel';

import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/use-toast';

interface HomeTabProps {
  bannerSlot: React.ReactNode;
  tournamentsSlot: React.ReactNode;
  jackpotSlot: React.ReactNode;
  initialCategories: CarouselData[];
}

const HomeTab: React.FC<HomeTabProps> = ({
  bannerSlot, tournamentsSlot, jackpotSlot, initialCategories,
}) => {
  const [categories, setCategories] = useState<CarouselData[]>(initialCategories);
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false);
  const [isWidgetReady, setIsWidgetReady] = useState<boolean>(false);
  const [isxtremepushSet, setIsxtremepushSet] = useState<boolean>(false);
  const widgetRef = useRef<MutableRefObject<any>>(null);
  const { toast } = useToast();
  const { data: session } = useSession();
  const { account } = useAuthStore();
  const { isSidebarOpen } = userUIStore();
  const searchParams = useSearchParams();
  const id = searchParams.get('id');
  const TxnId = searchParams.get('clientTransactionId');
  const cont = searchParams.get('cont');
  const emailVerification = searchParams.get('emailVerified');
  const unlimit = searchParams.get('deposit');
  const unlimitGateway = searchParams.get('gateway');
  const unlimitTxn = searchParams.get('txn');
  const isIntercom = searchParams.get('chat');
  const [userIdForRecommendations, setUserIdForRecommendations] = useState<string | null>(null);
  const [hasFetchedCategories, setHasFetchedCategories] = useState<boolean>(false);
  const scriptUrl = useMemo(() => { return process.env.NEXT_PUBLIC_SCRIPT_URL!; }, []);
  const integration = useMemo(() => { return process.env.NEXT_PUBLIC_ALTENAR_INTEGRATION!; }, []);

  const altenarInitConfig = useMemo(() => {
    return {
      integration,
      culture: 'es-ES',
      themeName: 'dark',
    };
  }, [integration]);

  // toast‑message maps can be static too
  const messagesLimits: Record<number, string> = useMemo(() => {
    return {
      1: messages.casinoLimit,
      2: messages.sportsLimit,
      3: messages.withdrawalLimit,
      4: messages.depositLimit,
    };
  }, []);

  const textVerified: Record<number, string> = useMemo(() => {
    return {
      1: 'Lo sentimos, El tiempo de verificación de tu correo electrónico ha expirado. Por favor, intenta nuevamente más tarde o contacta a soporte.',
      2: 'Tu correo ha sido verificado exitosamente. Si no has hecho tu primer depósito, hazlo ahora para que disfrutes de toda la emoción!',
      3: 'Lo sentimos, no pudimos verificar tu correo electrónico. Por favor, intenta nuevamente más tarde o contacta a soporte.',
    };
  }, []);

  useEffect(() => {
    const getUserIdForRecommendations = async () => {
      if (session?.user?.data && session?.user?.tag) {
        const userIdForRec = await decrypt(session?.user?.data, session?.user?.tag);
        setUserIdForRecommendations(userIdForRec);
      }
    };
    getUserIdForRecommendations();
  }, [session?.user?.data, session?.user?.tag]);

  const urlBonusParam = searchParams.get(process.env.NEXT_PUBLIC_URL_VAR_BONUS_PARAM!);

  const filterCategories = (cat: CarouselData[], headline: string) => {
    return cat.filter((category: CarouselData) => { return category.headline !== headline; });
  };

  const updateCategories = (prevCategories: CarouselData[], favoriteCategory: CarouselData) => {
    const newCategories = filterCategories(prevCategories, 'Favoritos');
    newCategories.unshift(favoriteCategory);
    return newCategories;
  };

  useEffect(() => {
    if (isIntercom === 'true') {
      if (account) {
        Intercom({
          app_id: 'l2erqbk4',
          user_id: account.topup_id,
          name: account.name,
          email: account.email,
          company: 'Hondubet',
          created_at: account.createdAt ? Math.floor(new Date(account.createdAt).getTime() / 1000) : undefined,
          hide_default_launcher: true,
        });
      } else {
      // Unauthenticated user
        Intercom({
          app_id: 'l2erqbk4',
          company: 'Hondubet',
          hide_default_launcher: true,
        });
      }
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('show'); // Opens the Intercom Messenger
      }
    }
  }, [isIntercom, account]);

  useEffect(() => {
    const cancelUnlimit = async () => {
      if (unlimit === 'cancel' && unlimitGateway === 'unlimit' && unlimitTxn) {
        if (session?.user?.data && session?.user?.tag) {
          const userId = await decrypt(session?.user?.data, session?.user?.tag);
          const response = await cancelUnlimitTxn({
            id: Number(userId),
            deposit_id: unlimitTxn,
          });
          if (response.processed) {
            console.log(response.message);
          } else {
            console.log('Error al cancelar la transacción');
          }
        }
      }
    };
    cancelUnlimit();
  }, [unlimit, unlimitGateway, unlimitTxn, session]);

  useEffect(() => {
    if (cont && messagesLimits[Number(cont)]) {
      toast({
        title: 'Informacion',
        description: messagesLimits[Number(cont)],
      });
    }

    if (emailVerification) {
      toast({
        title: 'Informacion',
        description: textVerified[Number(emailVerification)],
        variant: emailVerification === '2' ? 'default' : 'destructive',
      });
    }

    if ((!urlBonusParam || urlBonusParam !== process.env.NEXT_PUBLIC_URL_BONUS_PARAM_VALUE) && !emailVerification) {
      window.history.replaceState({}, '', '/');
    }
  }, [cont, urlBonusParam, toast, emailVerification, messagesLimits, textVerified]);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!hasFetchedCategories && account.favoriteGames && account.favoriteGames.length > 0) {
        const favoritesData = await getDatGames(account.favoriteGames, 'home');
        const favoriteCategory: CarouselData = {
          headline: 'Favoritos',
          items: favoritesData.gameData,
          url: '/favoritos',
          icon: '',
          sectionPosition: 0,
        };

        setCategories((prevCategories: CarouselData[]) => { return updateCategories(prevCategories, favoriteCategory); });
        setHasFetchedCategories(true);
      }
    };

    fetchFavorites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.favoriteGames]);

  useEffect(() => {
    if ((window as any).xtremepush && !isxtremepushSet) {
      (window as any).xtremepush('event', 'pageview_home');
      setIsxtremepushSet(true);
    }
  }, [isxtremepushSet]);

  useEffect(() => {
    const initAltenar = async () => {
      (window as any).altenarWSDK?.init(altenarInitConfig);

      (widgetRef as any).current = (window as any).altenarWSDK.addWidget({
        widget: 'WTopEventsCarousel',
        props: {
          onEventSelect: (event: any) => {
            window.location.href = `/deportes?event=${event.id}`;
          },
          onOddSelect: (odd: any) => {
            window.location.href = `/deportes?event=${odd.eventId}`;
          },
          backgroundOverride: {
            sport: {
              66: 'https://cdn.sanity.io/images/mavwf57a/production/413b526a262b3e12b000b3671d779c58362de3e1-400x141.webp',
              67: 'https://cdn.sanity.io/images/mavwf57a/production/0cb2696d8a088415ee2cdb14e152e9247db47b77-1792x1024.webp',
              68: 'https://cdn.sanity.io/images/mavwf57a/production/8c8e1b0d1e029261a620e0890aee4897eeb87ba2-1792x1024.webp',
              76: 'https://cdn.sanity.io/images/mavwf57a/production/2b74598240c46346d5717df0446ad3a50a3fd75b-1792x1024.webp',
            },
            championship: {
              2936: 'https://cdn.sanity.io/images/mavwf57a/production/5a5b7177a6a5351e49f8247395e92be6f6d91b7a-400x141.png',
              2941: 'https://cdn.sanity.io/images/mavwf57a/production/9554bdda86eeadf7e69113191546c01fe53b3954-400x141.png',
              2942: 'https://cdn.sanity.io/images/mavwf57a/production/0ad213d3f09f1d47364f9034ceab83e38a6266f1-400x141.png',
              16808:
              'https://cdn.sanity.io/images/mavwf57a/production/052d85e593399ca302da16156cf0df13eed2af99-400x141.png',
              3709: 'https://cdn.sanity.io/images/mavwf57a/production/10497278e15253bc8f7b139d92acd5c27a8e1070-400x141.png',
              3685: 'https://cdn.sanity.io/images/mavwf57a/production/b0ad17f6532a9445022289c9e822433cb7006b3a-400x141.png',
              17135:
              'https://cdn.sanity.io/images/mavwf57a/production/14305ec6ee0f818e5a7079a3620891dbc0dba422-400x141.png',
              32729:
              'https://cdn.sanity.io/images/mavwf57a/production/14305ec6ee0f818e5a7079a3620891dbc0dba422-400x141.png',
            },
          },
          scrollerControls: 3,
          showScorePreview: true,
          variants: {
            BannerEventBox: 4,
          },
        },
        tokens: {
          OddBox: {
            borderWidth: 1,
            borderColor: '#93346a00',
            background: '#0164abba',
            oddColor: '#95fb3fe6',
            labelColor: '#95fb3fe6',
          },
          OddBoxSelected: {
            background: '#109fe9c7',
            oddColor: '#95fb3fe6',
            labelColor: '#95fb3fe6',
          },
        },
        container: document.getElementById('topscores'),
      });
      setIsWidgetReady(true);
    };
    if (isScriptLoaded) {
      initAltenar();
    }

    return () => {
      if ((widgetRef as any).current) {
        (widgetRef as any).current.remove();
      }
    };
  }, [integration, isScriptLoaded, altenarInitConfig]);

  useEffect(() => {
    const checkConfirmation = async () => {
      if (id && TxnId) {
        const data = {
          id: Number(id),
          clientTxId: TxnId,
        };
        try {
          const response = await fetchConfirmationPayphone(data);
          if (response.processed) {
            toast({
              title: 'Depósito Creado',
              description: 'El depósito se ha creado exitosamente',
              variant: 'default',
              className: 'bg-sivarbet-secondary',
            });
          } else {
            console.log(
              'Error al confirmar el depósito / checkConfirmation method / response.processed',
            );
            toast({
              title: 'Error',
              description: 'Error al confirmar el depósito.',
              variant: 'destructive',
            });
          }
        } catch (error) {
          console.log(
            'Error al confirmar el depósito / checkConfirmation method: ',
            error,
          );
          toast({
            title: 'Error',
            description: 'Error al confirmar el depósito.',
            variant: 'destructive',
          });
        } finally {
          const url = new URL(window.location.href);
          url.search = '';
          window.history.replaceState({}, '', url.toString());
        }
      }
    };

    checkConfirmation();
  }, [id, TxnId, toast]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (typeof window !== 'undefined' && userIdForRecommendations) {
        const cachedRecommendations = localStorage.getItem('recommendations');
        const currentTime = Date.now();
        const cacheKey = 'recommendations';

        if (cachedRecommendations) {
          const parsedRecommendations = JSON.parse(cachedRecommendations);
          const { cacheTime } = parsedRecommendations;
          const isCacheValid = (currentTime - cacheTime) < 900000;

          if (!isCacheValid) {
            localStorage.removeItem(cacheKey);

            try {
              const response = await fetch('/api/casino/sectionsRecommendations');
              const enrichedRecommendations = await response.json();
              const { aggregatedSections } = enrichedRecommendations;

              localStorage.setItem('recommendations', JSON.stringify({
                data: aggregatedSections,
                cacheTime: currentTime,
              }));

              setCategories(aggregatedSections);
            } catch (error) {
              console.log('error fetching recommendations', error);
            }
          } else {
            console.log('valid cache, no update');
          }
        } else {
          localStorage.setItem('recommendations', JSON.stringify({
            data: categories,
            cacheTime: currentTime,
          }));
        }
      }
    };

    fetchRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdForRecommendations]);

  return (
    <>
      <Script
        src={`${scriptUrl}/altenarWSDK.js`}
        strategy="lazyOnload"
        onReady={() => {
          setIsScriptLoaded(true);
        }}
      />
      <div className={`${!isSidebarOpen ? 'pl-72' : 'pl-20'} w-full pt-20 max-lg:pl-0 max-lg:pt-[4.2rem]`}>

        <div
          className="text-sivarbet-text flex flex-col items-stretch w-full"
          id="mainTab"
          role="tabpanel"
          tabIndex={0}
        >
          {bannerSlot}
          <div className="mt-3">
            <Link href="/deportes" passHref className="flex items-center gap-2">
              <span className="icon icon-calendar text-l" />
              <h2 className="text-base sm:text-sm md:text-lg lg:text-xl">
                Eventos Populares
              </h2>
            </Link>
            <div id="topscores" className="mt-6 max-lg:mt-1" />
            {!isWidgetReady && (
              <div className="mt-6 max-lg:mt-1 flex gap-2">
                <Skeleton className="h-[134.5px] w-full sm:w-[400px] rounded-md bg-sivarbet-border" />
                <div className="hidden sm:flex gap-2">
                  <Skeleton className="h-[134.5px] w-[400px] rounded-md bg-sivarbet-border" />
                  <Skeleton className="h-[134.5px] w-[400px] rounded-md bg-sivarbet-border" />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-stretch mt-5 max-lg:mt-2 max-md:mt-2">
            <LeagueSlider />
          </div>
          <div className="w-full mt-5">
            {tournamentsSlot}
          </div>
          <div className="w-full mt-5">
            <Link prefetch={false} href="/jackpots" passHref className="flex items-baseline gap-2">
              <span className="icon icon-slot text-lg" />
              <h2 className="text-base sm:text-sm md:text-lg lg:text-xl font-medium text-sivarbet-text pb-3">
                Jackpot
              </h2>
            </Link>
            {jackpotSlot}
          </div>
          <div className="flex flex-col items-stretch w-full pt-5 max-lg:pt-0 max-md:pt-0">
            <SWSmoothCarousel data={categories || []} />
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(HomeTab);
